import { FC, Fragment, useEffect, useState } from "react"

import { Button, Checkbox, DatePicker, Drawer, Flex, Form, Input, Select, Space } from "antd"
import dayjs from "dayjs"

import { CustomRangePicker } from "../CustomRangePicker"

import { useFilterStorage } from "@/hook/useFilterStorage"

import {
  FilterField,
  FilterFieldOperator,
  FilterFieldType,
  IFilterProps,
  IFilterSelectField
} from "@/types/ui"

import { filterSelectOption } from "@/utils/filterSelectOption"


interface FilterWrapperProps extends IFilterProps {
  open: boolean
  setOpen: (isOpen: boolean) => void
  externalFilters?: FieldData[]
}

export interface FieldData {
  name: string | number | (string | number)[]
  value?: any
}

const numberOperators = [
  {
    id: FilterFieldOperator.EqualWithMathRound,
    name: "равно"
  },
  {
    id: FilterFieldOperator.GreaterThan,
    name: "больше"
  },
  {
    id: FilterFieldOperator.GreaterThanOrEqual,
    name: "больше или равно"
  },
  {
    id: FilterFieldOperator.LessThan,
    name: "меньше"
  },
  {
    id: FilterFieldOperator.LessThanOrEqual,
    name: "меньше или равно"
  }
]

export const numberOperatorName = "-numberOperator"

export const getNumberOperator = (fields: FieldData[], name: string) => {
  return fields.find(f => f.name == name + numberOperatorName)?.value
}

export const isFieldExist = (fields: FieldData[], name: string) => {
  return fields.find(f => f.name == name) != undefined
}

export const mapFilters = (filters: FilterField[], oldFilters: FieldData[]) => {
  return {
      filters: filters.map(f => {
        const currentFilter = oldFilters.find(
          i => (i.name as string[])[0] === f.name
        )

        if (!currentFilter || currentFilter.value === undefined || currentFilter.value === null) {
          return f
        }

        switch (f.type) {
          case FilterFieldType.Date:
            return {
              ...f, value: dayjs(new Date(currentFilter.value as string))
            }
          case FilterFieldType.DateRange:
            return {
              ...f, value: [
                dayjs(new Date(currentFilter.value[0] as string)),
                dayjs(new Date(currentFilter.value[1] as string))
              ]
            }
          case FilterFieldType.NumberWithOperator:
            return {
              ...f, 
              value: currentFilter.value, operator: getNumberOperator(oldFilters, f.name)
            }
          default:
            return {
              ...f, 
              value: currentFilter.value
            }
        }
      })
    }
}

export const FilterWrapper: FC<FilterWrapperProps> = (props) => {
  const [fields, setFields] = useState<FieldData[]>([])
  const [form] = Form.useForm()

  const {
    open,
    filters,
    filterKey,
    setOpen,
    onSubmit,
    onCancel,
    submitText = "Применить",
    cancelText = "Сбросить",
    externalFilters = []
  } = props

  const { saveFilter } = useFilterStorage({
    key: filterKey
  })

  const closeHandler = () => {
    setOpen(false)
  }

  const setFormValues = () => {
    filters.forEach(f => {
      switch (f.type) {
        case FilterFieldType.NumberWithOperator:
          {
            form.setFieldValue(f.name, f.value)
            form.setFieldValue(f.name + numberOperatorName, f.operator)
            if (!isFieldExist(fields, f.name)) {
              fields.push({ name: [f.name], value: f.value })
              fields.push({ name: [f.name + numberOperatorName], value: f.operator })
            }
            break
          }
        default:
          {
            form.setFieldValue(f.name, f.value)
            if (!isFieldExist(fields, f.name)) {
               fields.push({ name: [f.name], value: f.value })
            }
            break
          }
      }
    })
  }

  useEffect(() => {
    if (open) {
      // чтобы предзаполнить из сохраненных фильтров селект "больше, меньше, равно"
      setFormValues()
    }
  }, [open])

  useEffect(() => {
    if (externalFilters && externalFilters.length > 0 && filters && filters.length > 0) {
      setFormValues()
      submitHandler()
    }
  }, [externalFilters])

  const generateStringValueFromFilters = (data: FieldData[]): string => {
    if (!filters || filters.length === 0) return ""

    const expressions: string[][] = []
    const combinedData = [...data, ...externalFilters]

    for (const field of combinedData) {
      if (field.value == null || field.value === "" || field.name.toString().includes(numberOperatorName)) 
        continue

      const name = (field.name as string[])[0]
      const filter = filters.find(f => f.name === name)
      if (filter == null) {
        throw new Error(`Filter not found: ${name}`)
      }

      const operator = filter?.operator ?? FilterFieldOperator.Equal
      const type = filter.type

      if (Array.isArray(field.value) && type !== FilterFieldType.DateRange && type !== FilterFieldType.MultiSelect) {
        const tempExpression: string[] = []

        for (const exp of field.value) {
          tempExpression.push(transformValue(name, exp, type, operator))
        }

        expressions.push(tempExpression)
      } else {
        expressions.push([transformValue(name, field.value, type, operator, filter.equalAccuracy)])
      }
    }

    return expressions.join(",")
  }

  const transformValue = (name: string, value: any, type: FilterFieldType, operator: FilterFieldOperator, equalAccuracy?: number) => {
    switch (type) {
      case FilterFieldType.Date:
        return getStringForDate(name, value, operator) as string
      case FilterFieldType.DateRange:
        return getStringForDateRange(name, value) as string
      case FilterFieldType.Number:
        return `${name} ${transformOperator(operator)} ${value.replace(/\.(0+)$/, "").replace(/,/g, "\\,")}`
      case FilterFieldType.NumberWithOperator:
        return `${name} ${transformOperator(getNumberOperator(fields, name) ?? operator, equalAccuracy)} ${value.replace(/\.(0+)$/, "").replace(/,/g, "\\,")}`
      case FilterFieldType.MultiSelect:
        if (Array.isArray(value) && value.length > 0) {
          const orConditions = value
            .map(v => `${name} ${transformOperator(operator)} ${v}`)
            .join(" | ")
          return `(${orConditions})`
        }
        return ""
      case FilterFieldType.Checkbox:
        return `${name} ${transformOperator(operator)} ${value}`
      default:
        return `${name} ${transformOperator(operator)} ${value}`
    }
  }

  const getStringForDate = (name: string, value: any, operator: FilterFieldOperator) => {
    switch (operator) {
      case FilterFieldOperator.LessThan || FilterFieldOperator.LessThanOrEqual:
        return `${name} ${transformOperator(operator)} ${new Date(value).toLocaleDateString("ru-RU")} 23:59:59`
      case FilterFieldOperator.Equal:
        return `(${name} ${transformOperator(FilterFieldOperator.GreaterThanOrEqual)} ${new Date(value).toLocaleDateString("ru-RU")} 00:00:00, `
          + `${name} ${transformOperator(FilterFieldOperator.LessThanOrEqual)} ${new Date(value).toLocaleDateString("ru-RU")} 23:59:59)`
      default:
        return `${name} ${transformOperator(operator)} ${new Date(value).toLocaleDateString("ru-RU")} 00:00:00`
    }
  }

  const getStringForDateRange = (name: string, value: any) => {
    const toDateFilter = `${name} ${transformOperator(FilterFieldOperator.LessThanOrEqual)} ${value[1]
      ? new Date(value[1]).toLocaleDateString("ru-RU")
      : new Date().toLocaleDateString("ru-RU")} 23:59:59`
    return value[0] ? 
      `(${name} ${transformOperator(FilterFieldOperator.GreaterThanOrEqual)} ${new Date(value[0]).toLocaleDateString("ru-RU")} 00:00:00, ${toDateFilter})`
      : toDateFilter
  }

  const transformOperator = (operator: FilterFieldOperator, equalAccuracy?: number): string => {
    switch (operator) {
      case FilterFieldOperator.Equal:
        return "="
      case FilterFieldOperator.NotEqual:
        return "!="
      case FilterFieldOperator.GreaterThan:
        return ">"
      case FilterFieldOperator.LessThan:
        return "<"
      case FilterFieldOperator.Contains:
        return "=*"
      case FilterFieldOperator.GreaterThanOrEqual:
        return ">="
      case FilterFieldOperator.LessThanOrEqual:
        return "<="
      case FilterFieldOperator.EqualWithMathRound:
        let defaultAccuracy = 2
        let accuracy = Number(equalAccuracy) > 2 ? equalAccuracy : defaultAccuracy
        return `#=R(${accuracy})`
      default:
        return "="
    }
  }

  const submitHandler = () => {
    if (onSubmit) {
      onSubmit(generateStringValueFromFilters(fields))
      saveFilter(fields)
    }
  }

  const cancelHandler = () => {
    if (onCancel) {
      form.resetFields()
      setFields([])
      onCancel()
    }
  }

  const onChange = (fields: FieldData[]) => {
    setFields(fields)
  }

  return (
    <Drawer
      forceRender
      open={open}
      title="Фильтр"
      onClose={closeHandler}
      footer={
        <Flex vertical gap="small">
          <Button
            block
            size="large"
            type="primary"
            onClick={submitHandler}
          >
            {submitText}
          </Button>
          <Button
            block
            size="large"
            color="primary"
            variant="outlined"
            onClick={cancelHandler}
          >
            {cancelText}
          </Button>
        </Flex>
      }
    >
      <Form
        form={form}
        fields={filters}
        layout="vertical"
        size="large"
        onFieldsChange={(_, allFields) => {
          onChange(allFields)
        }}
      >
        {filters.map((m) => (
          <Fragment key={m.name}>
            {m.type === FilterFieldType.Text && (
              <Form.Item name={m.name} label={m.text} hidden={m.hidden}>
                <Input placeholder={m.text} />
              </Form.Item>
            )}
            {m.type === FilterFieldType.Select && (
              <Form.Item name={m.name} label={m.text} hidden={m.hidden}>
                <Select
                  allowClear
                  placeholder={m.text}
                  showSearch
                  options={(m as IFilterSelectField)?.values?.map(val => ({
                    value: val.id,
                    label: val.name
                  }))}
                  filterOption={(inputValue, option) => filterSelectOption(inputValue, option?.label || "")}
                />
              </Form.Item>
            )}
            {m.type === FilterFieldType.MultiSelect && (
              <Form.Item name={m.name} label={m.text} hidden={m.hidden}>
                <Select
                  mode="multiple"
                  placeholder={m.text}
                  showSearch
                  options={(m as IFilterSelectField)?.values?.map(val => ({
                    value: val.id,
                    label: val.name
                  }))}
                  filterOption={(inputValue, option) => filterSelectOption(inputValue, option?.label || "")}
                />
              </Form.Item>
            )}
            {m.type === FilterFieldType.Date && (
              <Form.Item name={m.name} label={m.text} hidden={m.hidden}>
                <DatePicker
                  format="DD.MM.YYYY"
                  style={{ width: "100%" }}
                  placeholder={m.text}
                />
              </Form.Item>
            )}
            {m.type === FilterFieldType.Number && (
              <Form.Item name={m.name} label={m.text}>
                <Input placeholder={m.text} type="number" />
              </Form.Item>
            )}
            {m.type === FilterFieldType.NumberWithOperator && (
              <Form.Item label={m.text} hidden={m.hidden}>
                <Space.Compact style={{ width: "-webkit-fill-available" }}>
                  <Form.Item
                    name={m.name + numberOperatorName}
                    initialValue={m.operator}
                    noStyle
                  >
                    <Select
                      placeholder="Сравнение"
                      options={numberOperators.map(val => ({
                        value: val.id,
                        label: val.name
                      }))}
                      filterOption={(inputValue, option) => filterSelectOption(inputValue, option?.label || "")}
                    />
                  </Form.Item>

                  <Form.Item name={m.name} noStyle>
                    <Input placeholder={m.text} type="number" />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            )}
            {m.type === FilterFieldType.DateRange && (
              <Form.Item name={m.name} label={m.text} hidden={m.hidden}>
                <CustomRangePicker style={{ width: "100%" }}  />
              </Form.Item>
            )}
            {m.type === FilterFieldType.Checkbox && (
              <Form.Item name={m.name} valuePropName="checked" hidden={m.hidden}>
                <Checkbox>{m.text}</Checkbox>
              </Form.Item>
            )}
          </Fragment>
        ))}
      </Form>
    </Drawer>
  )
}